import React from 'react';

/**
 * En-tête de page admin harmonisé (bandeau dégradé, icône, titre, actions, zone outils).
 */
export function AdminPageHeader({ theme, icon: Icon, title, subtitle, actions, footer }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border shadow-sm"
      style={{ backgroundColor: theme.surface, borderColor: theme.border }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{
          background: `linear-gradient(90deg, ${theme.accent} 0%, ${theme.accentGlow} 48%, ${theme.accent} 100%)`,
        }}
      />
      <div className="relative">
        <div className="flex flex-col gap-3 p-4 sm:p-5 lg:p-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
            {Icon ? (
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1 ring-black/[0.05] dark:ring-white/[0.08]"
                style={{ backgroundColor: theme.accentSoft, color: theme.accent }}
              >
                <Icon className="h-6 w-6" strokeWidth={1.75} />
              </div>
            ) : null}
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <h1 className="text-xl font-bold tracking-tight sm:text-2xl" style={{ color: theme.text }}>
                  {title}
                </h1>
                {actions ? (
                  <div className="flex shrink-0 flex-wrap items-center gap-2 sm:ml-4">{actions}</div>
                ) : null}
              </div>
              {subtitle ? (
                <p className="mt-1.5 max-w-3xl text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        {footer ? (
          <div
            className="border-t px-4 py-4 sm:px-5 sm:py-4 lg:px-6"
            style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Panneau de contenu (filtres, liste, formulaire) avec en-tête optionnel.
 */
export function AdminSection({ theme, title, description, icon: Icon, headerRight, children, className = '' }) {
  const showHeader = title || description || Icon || headerRight;
  return (
    <section
      className={`rounded-2xl border shadow-sm overflow-hidden ${className}`.trim()}
      style={{ backgroundColor: theme.surface, borderColor: theme.border }}
    >
      {showHeader ? (
        <div
          className="flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4"
          style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
        >
          <div className="flex min-w-0 items-start gap-3">
            {Icon ? (
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]"
                style={{ backgroundColor: theme.accentSoft, color: theme.accent }}
              >
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
            ) : null}
            <div className="min-w-0">
              {title ? (
                <h2 className="text-base font-semibold sm:text-lg tracking-tight" style={{ color: theme.text }}>
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p className="mt-0.5 text-xs sm:text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                  {description}
                </p>
              ) : null}
            </div>
          </div>
          {headerRight ? <div className="flex shrink-0 flex-wrap items-center gap-2">{headerRight}</div> : null}
        </div>
      ) : null}
      <div className="p-4 sm:p-5 lg:p-6">{children}</div>
    </section>
  );
}

/** Props à fusionner sur <TabsList /> pour un style admin cohérent. */
export function adminTabsListProps(theme, extraClassName = '') {
  return {
    className: `rounded-xl border p-1.5 gap-1 shadow-sm ${extraClassName}`.trim(),
    style: { borderColor: theme.border, backgroundColor: theme.inputBackground },
  };
}

/**
 * Enveloppe pour la barre d’onglets de l’éditeur de devis : défilement horizontal confortable sur mobile.
 */
export const devisQuoteTabsScrollClass =
  'w-full min-w-0 -mx-1 px-1 overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-smooth pb-1 touch-pan-x';

export const adminPageStackClass = 'space-y-5 w-full min-w-0';
